/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { isUserAllowed } from "../../helpers/user";
import { clearCookie, getCookie } from "../../api/apiClient";
import { SendLogoutApiRequest } from "./Logout";

interface RequireAuthProps {
  children: JSX.Element;
}

const REDIRECT_URL_KEY = "redirectUrl";

const RequireAuth: React.FC<RequireAuthProps> = ({ children }) => {
  const location = useLocation();
  const isAuthenticated = checkAuthentication();

  useEffect(() => {
    if (isAuthenticated) {
      const redirectUrl = localStorage.getItem(REDIRECT_URL_KEY);

      if (redirectUrl) {
        localStorage.removeItem(REDIRECT_URL_KEY);
        if (redirectUrl !== "/" && redirectUrl !== "/login") {
          window.location.href = redirectUrl;
        }
      }
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    localStorage.setItem(REDIRECT_URL_KEY, location.pathname + location.search);
    return <Navigate to="/login" />;
  }

  return children;
};

export const checkAuthentication = (): boolean => {
  const sessionExpiryString = getCookie("session_expiry");
  if (!sessionExpiryString) return false;

  const sessionExpiry = new Date(sessionExpiryString);
  const now = new Date();

  if (now > sessionExpiry) {
    localStorage.removeItem("last_viewed_engagement");
    return false;
  }

  const accessEnabled = isUserAllowed();

  if (!accessEnabled) {
    window.alert(
      "Your access to the portal has been disabled. Please contact your Organization Administrator."
    );
    SendLogoutApiRequest();
    return false;
  }

  return true;
};

export default RequireAuth;
